import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus, FileCode, Trash2 } from 'lucide-react'
import { useScriptStore } from '@/store/scriptStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useQuery } from '@tanstack/react-query'
import { scriptsApi } from '@/services/api/scripts'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import { useTranslation } from 'react-i18next'
import { FilterBar, ActiveFilters, FilterState, FilterConfig, applyFilters } from '@/components/ui/filters'
import type { Script } from '@/types/script'

export default function ScriptListPage() {
  const { t } = useTranslation()
  const { scripts, loadScripts, deleteScript, isLoading } = useScriptStore()
  const [page, setPage] = useState(1)
  const pageSize = 12 // Fixed page size for now
  const [filterState, setFilterState] = useState<FilterState>({
    search: '',
    selectedOptions: {},
    dateRange: null,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  })

  // Filter configurations
  const filterConfigs: FilterConfig[] = [
    {
      key: 'search',
      label: 'Search',
      type: 'search',
      placeholder: 'Search scripts by name or description...',
    },
    {
      key: 'type',
      label: 'Type',
      type: 'multiselect',
      options: [
        { value: 'regular', label: 'Regular Scripts', count: scripts.filter(s => !s.isTemplate && !s.isPublic).length },
        { value: 'template', label: 'Templates', count: scripts.filter(s => s.isTemplate).length },
        { value: 'public', label: 'Public Scripts', count: scripts.filter(s => s.isPublic).length },
      ],
    },
    {
      key: 'hasDataset',
      label: 'Dataset',
      type: 'select',
      options: [
        { value: 'with_dataset', label: 'With Dataset', count: scripts.filter(s => s.selectedDatasetId).length },
        { value: 'without_dataset', label: 'Without Dataset', count: scripts.filter(s => !s.selectedDatasetId).length },
      ],
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'daterange',
    },
    {
      key: 'sort',
      label: 'Sort',
      type: 'sort',
      options: [
        { value: 'name', label: 'Name' },
        { value: 'createdAt', label: 'Date Created' },
        { value: 'updatedAt', label: 'Date Updated' },
      ],
    },
  ]

  // Filter functions
  const filterFunctions = {
    type: (script: Script, value: string) => {
      switch (value) {
        case 'regular': return !script.isTemplate && !script.isPublic
        case 'template': return !!script.isTemplate
        case 'public': return !!script.isPublic
        default: return true
      }
    },
    hasDataset: (script: Script, value: string) => {
      switch (value) {
        case 'with_dataset': return !!script.selectedDatasetId
        case 'without_dataset': return !script.selectedDatasetId
        default: return true
      }
    },
    dateRange: (script: Script, dateRange: any) => {
      if (!dateRange || (!dateRange.from && !dateRange.to)) return true;
      const scriptDate = new Date(script.createdAt);
      if (dateRange.from && scriptDate < dateRange.from) return false;
      if (dateRange.to && scriptDate > dateRange.to) return false;
      return true;
    },
    sort: (a: Script, b: Script, sortBy: string) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'createdAt':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'updatedAt':
          return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        default:
          return 0
      }
    },
  }

  // Apply filters to scripts
  const filteredScripts = useMemo(() => {
    return applyFilters(scripts, filterState, filterFunctions)
  }, [scripts, filterState])

  // Paginate filtered results
  const paginatedScripts = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredScripts.slice(startIndex, endIndex)
  }, [filteredScripts, page, pageSize])

  const totalPages = Math.ceil(filteredScripts.length / pageSize)

  const { data: queryResult, refetch, isLoading: queryLoading, error } = useQuery({
    queryKey: ['scripts', page, pageSize, filterState],
    queryFn: async () => {
      // Build API options from current filters
      const apiOptions: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }

      // Add search filter
      if (filterState.search) {
        apiOptions.search = filterState.search
      }

      // Add selected options
      Object.entries(filterState.selectedOptions).forEach(([key, values]) => {
        if (values.length > 0) {
          if (key === 'type') {
            // Handle script types
            if (values.includes('regular')) {
              // Regular scripts (not template and not public)
              // This is complex, so we'll handle in frontend for now
            } else if (values.includes('template')) {
              apiOptions.isTemplate = true
            } else if (values.includes('public')) {
              apiOptions.isPublic = true
            }
          } else if (key === 'hasDataset') {
            apiOptions.hasDataset = values[0]
          }
        }
      })

      // Add date range - format as YYYY-MM-DD for backend
      if (filterState.dateRange) {
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startDate = filterState.dateRange.from ? formatDate(filterState.dateRange.from) : undefined;
        const endDate = filterState.dateRange.to ? formatDate(filterState.dateRange.to) : undefined;

        if (startDate) apiOptions.dateFrom = startDate;
        if (endDate) apiOptions.dateTo = endDate;
      }

      // Add sorting
      if (filterState.sortBy) {
        apiOptions.sortBy = filterState.sortBy
        apiOptions.sortOrder = filterState.sortOrder
      }

      return await scriptsApi.list(apiOptions)
    },
  })
  
  useEffect(() => {
    if (error) {
      showErrorToast(error as Error, t('errors.loadScripts'))
    }
  }, [error, t])

  useEffect(() => {
    if (queryResult?.scripts && Array.isArray(queryResult.scripts)) {
      // Update store with fetched data
      loadScripts()
    }
  }, [queryResult, loadScripts])

  // Note: We now use filtered scripts instead of raw query data

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (confirm(t('common.confirmDelete'))) {
      try {
        await deleteScript(id)
        refetch()
        showSuccessToast(t('scripts.deleteSuccess'))
      } catch (error) {
        showErrorToast(error, t('errors.deleteScript'))
      }
    }
  }

  if (queryLoading || isLoading) {
    return <LoadingPage message={t('common.loading')} />
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t('scripts.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('scripts.subtitle')}
          </p>
        </div>
        <Link to="/scripts/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            {t('scripts.newScript')}
          </Button>
        </Link>
      </div>

      {/* Filters */}
      {scripts.length > 0 && (
        <div className="space-y-4">
          <FilterBar
            filters={filterConfigs}
            onChange={setFilterState}
            initialState={filterState}
          />

          <ActiveFilters
            filters={filterConfigs}
            filterState={filterState}
            onRemove={(filterKey, value) => {
              if (filterKey === 'search') {
                setFilterState(prev => ({ ...prev, search: '' }))
              } else if (filterKey === 'daterange') {
                setFilterState(prev => ({ ...prev, dateRange: null }))
              } else if (value) {
                setFilterState(prev => ({
                  ...prev,
                  selectedOptions: {
                    ...prev.selectedOptions,
                    [filterKey]: prev.selectedOptions[filterKey]?.filter(v => v !== value) || []
                  }
                }))
              }
            }}
          />

          <div className="text-sm text-muted-foreground">
            Showing {paginatedScripts.length} of {filteredScripts.length} scripts
            {filteredScripts.length !== scripts.length && (
              <span> (filtered from {scripts.length} total)</span>
            )}
          </div>
        </div>
      )}

      {filteredScripts.length === 0 && scripts.length > 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileCode className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No scripts match your filters</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filter criteria
            </p>
            <Button
              variant="outline"
              onClick={() => setFilterState({
                search: '',
                selectedOptions: {},
                dateRange: null,
                sortBy: 'updatedAt',
                sortOrder: 'desc',
              })}
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : filteredScripts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileCode className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('scripts.noScripts')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('scripts.noScriptsDescription')}
            </p>
            <Link to="/scripts/new">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t('scripts.createScript')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedScripts.map((script) => (
              <div key={script.id} className="relative">
                <Link to={`/scripts/${script.id}`}>
                  <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="line-clamp-1">{script.name}</CardTitle>
                          <CardDescription className="mt-1">
                            {t('scripts.updated')} {new Date(script.updatedAt).toLocaleDateString()}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <FileCode className="w-4 h-4" />
                        <span className="line-clamp-2">
                          {script.dslCode.substring(0, 100)}
                          {script.dslCode.length > 100 ? '...' : ''}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => handleDelete(script.id, e)}
                  className="absolute top-2 right-2 h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-destructive hover:text-destructive-foreground"
                  title={t('common.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
